import parse from "html-react-parser"
import { useTranslation } from "react-i18next"

import { CourseList } from "@/features/training/components/course-list"
import {
  DemoAIParser,
  DemoParserTips,
  DemoParserUseCases,
  DemoParserWorkflow,
} from "@/features/training/components/demo"
import { CourseLayout } from "@/features/training/components/layouts/course-layout"
import { CourseSection } from "@/features/training/components/layouts/course-section"

type MainFlowStep = {
  title: string
  description: string
  details?: string[]
  tip?: string
  column_fields?: {
    intro: string
    fields: string[]
    good_descriptions_label: string
    good_descriptions: string[]
    can_do: string
    actions: string[]
  }
  when?: string[]
  parser_does?: string[]
  remember?: string
  table_info?: {
    intro: string
    shows: string[]
    use: string
  }
  use_features?: string[]
  export_info?: {
    intro: string
    can_do: string[]
    use_further: string
    destinations: string[]
  }
}

type UseCase = {
  title: string
  points: string[]
}

type Exercise = {
  title: string
  steps: string[]
}

export function AIParserGettingStartedPage() {
  const { t } = useTranslation("", { keyPrefix: "training.ai-parser.getting-started" })

  return (
    <CourseLayout>
      <CourseSection>
        <h1>{t("title")}</h1>
        <h2>{t("purpose.title")}</h2>
        <p>{parse(t("purpose.description"))}</p>
        <p>
          <strong>{t("purpose.level")}</strong>
        </p>
      </CourseSection>

      <CourseSection>
        <h2>{t("learning_objectives.title")}</h2>
        <p>{t("learning_objectives.description")}</p>
        <CourseList
          data={t("learning_objectives.objectives", { returnObjects: true }) as string[]}
        />
      </CourseSection>

      <CourseSection>
        <h2>{t("overview.title")}</h2>
        <p>{parse(t("overview.intro"))}</p>
        <CourseList data={t("overview.capabilities", { returnObjects: true }) as string[]} />
        <p>{parse(t("overview.analogy"))}</p>
        <h3>{t("workflow_demo.title")}</h3>
        <p>{t("workflow_demo.intro")}</p>
        <div className="my-4">
          <DemoParserWorkflow />
        </div>
      </CourseSection>

      <CourseSection>
        <h2>{t("interactive_demo.title")}</h2>
        <p>{parse(t("interactive_demo.description"))}</p>
        <div className="my-6">
          <DemoAIParser />
        </div>
        <blockquote className="border-primary bg-primary/15 border-l-4 p-4">
          <p>{parse(t("interactive_demo.tip"))}</p>
        </blockquote>
      </CourseSection>

      <CourseSection>
        <h2>{t("main_flow.title")}</h2>
        {/* Main flow involves multiple steps - best shown with screenshots of each stage */}
        {(t("main_flow.steps", { returnObjects: true }) as MainFlowStep[]).map((step, index) => (
          <div key={index}>
            <h3>{step.title}</h3>
            <p>{parse(step.description)}</p>
            {step.details && <CourseList data={step.details} />}
            {step.tip && (
              <blockquote className="border-primary bg-primary/15 border-l-4 p-4">
                <p>{parse(step.tip)}</p>
              </blockquote>
            )}
            {step.column_fields && (
              <>
                <p>{parse(step.column_fields.intro)}</p>
                <CourseList data={step.column_fields.fields} />
                <p>
                  <strong>{step.column_fields.good_descriptions_label}</strong>
                </p>
                <CourseList data={step.column_fields.good_descriptions} />
                <p>{parse(step.column_fields.can_do)}</p>
                <CourseList data={step.column_fields.actions} />
              </>
            )}
            {step.when && <CourseList data={step.when} />}
            {step.parser_does && <CourseList data={step.parser_does} />}
            {step.remember && (
              <blockquote className="border-primary bg-primary/15 border-l-4 p-4">
                <p>{parse(step.remember)}</p>
              </blockquote>
            )}
            {step.table_info && (
              <>
                <p>{parse(step.table_info.intro)}</p>
                <CourseList data={step.table_info.shows} />
                <p>{parse(step.table_info.use)}</p>
                <CourseList data={step.use_features} />
              </>
            )}
            {step.export_info && (
              <>
                <p>{parse(step.export_info.intro)}</p>
                <CourseList data={step.export_info.can_do} />
                <p>{parse(step.export_info.use_further)}</p>
                <CourseList data={step.export_info.destinations} />
              </>
            )}
          </div>
        ))}
      </CourseSection>

      <CourseSection>
        <h2>{t("templates.title")}</h2>
        <h3>{t("templates.why.title")}</h3>
        <p>{parse(t("templates.why.description"))}</p>
        <CourseList data={t("templates.why.benefits", { returnObjects: true }) as string[]} />

        <h3>{t("templates.create.title")}</h3>
        <CourseList data={t("templates.create.steps", { returnObjects: true }) as string[]} />

        <h3>{t("templates.use.title")}</h3>
        <CourseList data={t("templates.use.steps", { returnObjects: true }) as string[]} />

        <h3>{t("templates.manage.title")}</h3>
        <p>{parse(t("templates.manage.description"))}</p>
        <CourseList data={t("templates.manage.actions", { returnObjects: true }) as string[]} />
      </CourseSection>

      <CourseSection>
        <h2>{t("examples.title")}</h2>
        <h3>{t("use_cases_demo.title")}</h3>
        <p>{t("use_cases_demo.intro")}</p>
        <div className="my-4">
          <DemoParserUseCases />
        </div>
        {(t("examples.use_cases", { returnObjects: true }) as UseCase[]).map((useCase, index) => (
          <div key={index}>
            <h3>{useCase.title}</h3>
            <CourseList data={useCase.points} />
          </div>
        ))}
      </CourseSection>

      <CourseSection>
        <h2>{t("exercises.title")}</h2>
        {(t("exercises.items", { returnObjects: true }) as Exercise[]).map((exercise, index) => (
          <div key={index}>
            <h3>{exercise.title}</h3>
            <CourseList data={exercise.steps} />
          </div>
        ))}
      </CourseSection>

      <CourseSection>
        <h2>{t("pitfalls.title")}</h2>
        <div className="my-4">
          <DemoParserTips />
        </div>
        <CourseList data={t("pitfalls.tips", { returnObjects: true }) as string[]} />
      </CourseSection>

      <CourseSection>
        <h2>{t("when_to_use.title")}</h2>
        <p>
          <strong>{t("when_to_use.parser_label")}</strong>
        </p>
        <CourseList
          data={t("when_to_use.parser_situations", { returnObjects: true }) as string[]}
        />
        <p>
          <strong>{t("when_to_use.explorer_label")}</strong>
        </p>
        <CourseList
          data={t("when_to_use.explorer_situations", { returnObjects: true }) as string[]}
        />
        <p>
          <strong>{t("when_to_use.combination_label")}</strong>
        </p>
        <CourseList
          data={t("when_to_use.combination_steps", { returnObjects: true }) as string[]}
        />
      </CourseSection>

      <CourseSection>
        <h2>{t("summary.title")}</h2>
        <CourseList data={t("summary.points", { returnObjects: true }) as string[]} />
      </CourseSection>
    </CourseLayout>
  )
}
